f
<template>
  <div class="gam-input-birth-date">
    <div v-if="label" class="label">
      {{ label }}
    </div>
    <div class="input-group">
      <GamSelect v-bind="getMonth" v-model="month" />
      <GamSimpleInput v-bind="getDay" v-model="day" />
      <GamSimpleInput v-bind="getYear" v-model="year" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import { GamInputName, GamInputValueType } from '@/views/composables/constants/components/gamInput.constants';
import { computed, ref, watch } from 'vue';
import { GamComponentsEmits } from '../composables/constants/main/emit.constants';
import GamSelect, { type GamSelectProps } from './GamSelect.vue';
import GamSimpleInput from './GamSimpleInput.vue';

interface Props {
  label?: string;
  modelValue?: Date | string;
  isDisabled?: boolean;
  isError?: boolean;
}

const props = defineProps<Props>();
const emits = defineEmits([GamComponentsEmits.UPDATE_VALUE, GamComponentsEmits.UPDATE]);

const toParts = (value?: Date | string) => {
  const date = value ? new Date(value) : undefined;

  return {
    day: date ? date.getDate() : undefined,
    month: date ? date.getMonth() + 1 : undefined,
    year: date ? date.getFullYear() : undefined,
  };
};

const initial = toParts(props.modelValue);

const day = ref<number | undefined>(initial.day);
const month = ref<number | undefined>(initial.month);
const year = ref<number | undefined>(initial.year);

// The bound value also changes from the outside - a fresh form clears it - and
// without this the three inputs keep whatever they were mounted with, leaving a
// cleared date still on screen. The guard stops the emit below bouncing back.
watch(
  () => props.modelValue,
  (value) => {
    const parts = toParts(value);
    if (parts.day === day.value && parts.month === month.value && parts.year === year.value) return;

    day.value = parts.day;
    month.value = parts.month;
    year.value = parts.year;
  },
);

watch(
  () => [year.value, month.value, day.value],
  ([year, month, day]) => {
    if (!year || !month || !day) return;

    const date = new Date();
    date.setFullYear(year);
    date.setMonth(parseInt(month.toString(), 10) - 1);
    date.setDate(day);
    date.setHours(12);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);

    emits(GamComponentsEmits.UPDATE, date);
    emits(GamComponentsEmits.UPDATE_VALUE, date);
  },
);

const getDay = computed(() => {
  return {
    name: GamInputName.GAM_BIRTH_DAY,
    placeholder: localize('auth.page.registration.form.date.day.placeholder'),
    label: localize('auth.page.registration.form.date.day.placeholder'),
    value: day.value,
    type: GamInputValueType.NUMBER,
    // GamInput defaults this off, GamSimpleInput does not, so say it here.
    autocomplete: 'off',
    isSmall: true,
  };
});

const getYear = computed(() => {
  return {
    name: GamInputName.GAM_BIRTH_YEAR,
    placeholder: localize('auth.page.registration.form.date.year.placeholder'),
    label: localize('auth.page.registration.form.date.year.placeholder'),
    value: year.value,
    type: GamInputValueType.NUMBER,
    autocomplete: 'off',
    isMedium: true,
  };
});

const getMonth = computed(
  (): GamSelectProps => ({
    name: GamInputName.GAM_BIRTH_MONTH,
    label: 'auth.page.registration.form.date.month.placeholder',
    placeholder: 'auth.page.registration.form.date.month.placeholder',
    modelValue: month.value,
    options: getMonthOptions(),
  }),
);

const getMonthOptions = () => [
  { value: 1, label: localize('app.month.jan'), mobileLabel: localize('app.month.jan').substring(0, 3) },
  { value: 2, label: localize('app.month.feb'), mobileLabel: localize('app.month.feb').substring(0, 3) },
  { value: 3, label: localize('app.month.mar'), mobileLabel: localize('app.month.mar').substring(0, 3) },
  { value: 4, label: localize('app.month.apr'), mobileLabel: localize('app.month.apr').substring(0, 3) },
  { value: 5, label: localize('app.month.may'), mobileLabel: localize('app.month.may').substring(0, 3) },
  { value: 6, label: localize('app.month.jun'), mobileLabel: localize('app.month.jun').substring(0, 3) },
  { value: 7, label: localize('app.month.jul'), mobileLabel: localize('app.month.jul').substring(0, 3) },
  { value: 8, label: localize('app.month.aug'), mobileLabel: localize('app.month.aug').substring(0, 3) },
  { value: 9, label: localize('app.month.sep'), mobileLabel: localize('app.month.sep').substring(0, 3) },
  { value: 10, label: localize('app.month.oct'), mobileLabel: localize('app.month.oct').substring(0, 3) },
  { value: 11, label: localize('app.month.nov'), mobileLabel: localize('app.month.nov').substring(0, 3) },
  { value: 12, label: localize('app.month.dec'), mobileLabel: localize('app.month.dec').substring(0, 3) },
];
</script>

<style scoped lang="scss">
.is-small {
  max-width: 4.5rem;
}

.is-medium {
  max-width: 5rem;
}

.gam-input-birth-date {
  width: 100%;
  display: flex;
  position: relative;
  align-items: flex-start;
  flex-direction: column;
  gap: var(--spacing-md);
  flex: 1 0 0;

  .label {
    font-size: 14px;
    font-weight: 400;
    color: var(--color-white-70);
    line-height: 110%;
  }

  .input-group {
    display: grid;
    width: 100%;
    grid-template-columns: auto 70px 75px;
    grid-column-gap: 0.5rem;
  }
}
</style>
